/**
 * @fileoverview Dashboards scenario detector for CopilotBar.
 *
 * Handles CopilotBar configuration when the user is in the Dashboards section
 * of the app (main Dashboard page at /dashboard).
 *
 * @module CopilotBar/scenarios/dashboards/dashboardsScenario
 */

import { COPILOT_CHAT_FEATURES, COPILOT_RESPONSE_TYPE } from '@Sage/ssm-web5';

import { ANALYTICS_REPORT, DASHBOARD } from 'constants/Entities';
import { COPILOT_DOMAINS } from 'utils/copilotRegistry/constants';
import { getLiteral } from 'utils/getLiteral';

import { COPILOT_BAR_SCENARIOS } from '../constants';
import { getChatId } from '../utils';

/**
 * Scenario detector for the Dashboards section.
 *
 * This scenario is activated when:
 * - The current pathname is `/dashboard` (main Dashboards section)
 * - The active dashboard is ForceBI type (via featureMethods.ssm.isForceBIDashboard)
 *
 * When active, the CopilotBar is displayed as a side panel with:
 * - General SAGE_COPILOT assistant (no dashboards-specific feature)
 * - Pinned to the right side of the viewport
 *
 * @type {Object}
 * @property {string} scenario - The scenario type identifier (DASHBOARDS)
 * @property {Function} condition - Evaluates if the user is on the Dashboards page
 * @property {Function} buildConfig - Builds the CopilotBar configuration for Dashboards context
 */
export const dashboardsScenario = {
    buildConfig: ({ setCopilotBarProps }) => {
        const defaultLoadingPhrase = getLiteral('label_danai_thinking_v3');
        let lastFeedbackText = '';

        return {
            chatSetup: {
                cb: {
                    onAnswer: ({ type, text }) => {
                        if (text) {
                            lastFeedbackText = text;
                            setCopilotBarProps({
                                feedback: {
                                    message: text,
                                    isStreaming: type === COPILOT_RESPONSE_TYPE.TEXT_TYPE,
                                },
                            });
                        }
                    },
                    onFinish: (payload) => {
                        const feedbackText = payload?.text || lastFeedbackText;
                        if (feedbackText) {
                            setCopilotBarProps({
                                feedback: {
                                    message: feedbackText,
                                    dismissible: true,
                                },
                            });
                        }
                    },
                },
                chatId: getChatId({
                    feature: COPILOT_CHAT_FEATURES.BUSINESS_INTELLIGENCE,
                    name: 'dashboards',
                }),
                session: {
                    features: [COPILOT_CHAT_FEATURES.BUSINESS_INTELLIGENCE],
                },
            },
            initialLoadingPhrase: defaultLoadingPhrase,
            ignoreCopilotSidebarInset: false,
            inputPlaceholder: getLiteral('placeholder_copilot_bar_dashboards'),
            isOpen: true,
            loadingPhrases: [defaultLoadingPhrase],
            pinnedToRight: false,
            showInputModeControls: false,
            transition: 'slideUp',
            width: 400,
        };
    },
    condition: ({ pathname, featureMethods }) => {
        const isDashboardsPath =
            pathname === DASHBOARD.route || pathname.startsWith(ANALYTICS_REPORT.route);

        return isDashboardsPath && featureMethods?.[COPILOT_DOMAINS.SSM]?.isForceBIDashboard?.();
    },
    scenario: COPILOT_BAR_SCENARIOS.DASHBOARDS,
};
